/**
 * MiTrabajo.js v4.4.33 — Workspace personal del técnico
 *
 * Vista unificada de TODO lo asignado al usuario actual:
 *   - Oportunidades + Licitaciones + Leads en un solo dashboard
 *   - Plazos próximos a vencer (alerta urgente)
 *   - Últimas vistas (donde estaba antes de salir)
 *   - Resumen por estado/etapa
 *
 * Objetivo: cuando el técnico vuelve, en 30 segundos sabe qué tenía
 * a medias. Sin perder 10-30 min recuperando contexto.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import {
  Briefcase,
  Target,
  FileSearch,
  Users,
  Clock,
  AlertTriangle,
  RefreshCw,
  History,
  CheckCircle2,
  Eye,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import DateRangeFilter from "@/components/DateRangeFilter";

const formatEUR = (n) =>
  n
    ? new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

const formatFecha = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
};

const diasHasta = (iso) => {
  if (!iso) return null;
  const target = new Date(String(iso).slice(0, 10));
  const hoy = new Date(new Date().toISOString().slice(0, 10));
  const ms = target.getTime() - hoy.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const ESTADO_COLORS = {
  nueva: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40",
  en_evaluacion: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",
  en_curso: "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/40",
  publicada: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
  promovida: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/40",
  ganada: "bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 border-emerald-600/50",
  perdida: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40",
  descartada: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30",
};

export default function MiTrabajo() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [incluirCompletadas, setIncluirCompletadas] = useState(false);
  // v4.4.33: filtros fecha por sección
  const [fechaOportunidades, setFechaOportunidades] = useState({ desde: null, hasta: null });
  const [fechaLicitaciones, setFechaLicitaciones] = useState({ desde: null, hasta: null });
  const [fechaLeads, setFechaLeads] = useState({ desde: null, hasta: null });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/mi-trabajo", {
        params: { incluir_completadas: incluirCompletadas },
      });
      setData(r.data);
    } catch (e) {
      console.error("Error mi-trabajo:", e);
      toast.error("Error cargando mi trabajo");
    } finally {
      setLoading(false);
    }
  }, [incluirCompletadas]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const irA = (tipo, id, titulo) => {
    // Registrar vista (fire-and-forget)
    api.post("/mi-trabajo/track-view", {
      item_type: tipo,
      item_id: String(id),
      item_title: titulo || "(sin título)",
    }).catch(() => {});
    // Navegar (el deep-link abre el modal en la página correspondiente)
    if (tipo === "oportunidad") navigate(`/oportunidades?open=${encodeURIComponent(id)}`);
    else if (tipo === "licitacion") navigate(`/licitaciones?open=${encodeURIComponent(id)}`);
    else if (tipo === "lead") navigate(`/leads?open=${encodeURIComponent(id)}`);
  };

  const resumen = data?.resumen;
  // Helper para filtrar por rango de fechas en cliente
  const enRango = (iso, rango) => {
    if (!rango?.desde && !rango?.hasta) return true;
    if (!iso) return false;
    const fecha = String(iso).slice(0, 10);
    if (rango.desde && fecha < rango.desde) return false;
    if (rango.hasta && fecha > rango.hasta) return false;
    return true;
  };
  const oportunidades = (data?.mis_oportunidades || []).filter((o) =>
    enRango(o.fecha_adjudicacion || o.fecha_deteccion, fechaOportunidades)
  );
  const licitaciones = (data?.mis_licitaciones || []).filter((l) =>
    enRango(l.fecha_limite_presentacion || l.fecha_publicacion, fechaLicitaciones)
  );
  const leads = (data?.mis_leads || []).filter((ld) =>
    enRango(ld.updated_at || ld.created_at, fechaLeads)
  );
  const plazos = data?.plazos_proximos || [];
  const recientes = data?.recientes || [];

  const userName = useMemo(() => {
    return data?.user?.name || data?.user?.email?.split("@")[0] || "técnico";
  }, [data]);

  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-teal-500/15 border border-teal-500/40">
            <Briefcase className="w-6 h-6 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold theme-text">Mi Trabajo</h1>
            <p className="theme-text-muted text-sm">
              Tu espacio personal, {userName}. Aquí está todo lo que tienes
              asignado en un solo sitio.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIncluirCompletadas((v) => !v)}
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {incluirCompletadas ? "Ocultar cerradas" : "Incluir cerradas"}
          </Button>
          <Button variant="outline" size="sm" onClick={cargar} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Target className="w-3.5 h-3.5 text-red-500" /> Oportunidades
            </div>
            <div className="text-2xl font-bold theme-text">
              {resumen?.total_oportunidades ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <FileSearch className="w-3.5 h-3.5 text-blue-500" /> Licitaciones
            </div>
            <div className="text-2xl font-bold theme-text">
              {resumen?.total_licitaciones ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Users className="w-3.5 h-3.5 text-purple-500" /> Leads
            </div>
            <div className="text-2xl font-bold theme-text">
              {resumen?.total_leads ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> Urgentes
            </div>
            <div className="text-2xl font-bold theme-text">
              {resumen?.plazos_urgentes ?? "—"}
            </div>
            <div className="text-xs theme-text-muted">≤3 días</div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Clock className="w-3.5 h-3.5 text-amber-500" /> Plazos prox.
            </div>
            <div className="text-2xl font-bold theme-text">
              {resumen?.plazos_proximos ?? "—"}
            </div>
            <div className="text-xs theme-text-muted">14 días</div>
          </CardContent>
        </Card>
      </div>

      {/* PLAZOS URGENTES (banner si hay alguno ≤7 días) */}
      {plazos.length > 0 && (
        <Card className="mb-4 border-amber-500/40 theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <h3 className="font-semibold theme-text">
                Plazos próximos a vencer
              </h3>
            </div>
            <div className="space-y-2">
              {plazos.slice(0, 8).map((p, i) => (
                <button
                  key={i}
                  onClick={() => irA(p.tipo, p.id, p.titulo)}
                  className="w-full text-left flex items-center gap-3 p-2 rounded hover:theme-bg-tertiary transition"
                >
                  <Badge
                    className={`text-xs whitespace-nowrap ${
                      p.dias_restantes <= 1
                        ? "bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/50"
                        : p.dias_restantes <= 3
                        ? "bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/50"
                        : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40"
                    }`}
                  >
                    {p.dias_restantes === 0 ? "HOY" :
                     p.dias_restantes === 1 ? "MAÑANA" :
                     `${p.dias_restantes}d`}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="theme-text text-sm truncate">{p.titulo}</div>
                    <div className="theme-text-muted text-xs truncate">
                      {p.organo}
                    </div>
                  </div>
                  <div className="theme-text-muted text-xs font-mono whitespace-nowrap">
                    {formatEUR(p.importe)}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs principal */}
      <Tabs defaultValue="oportunidades" className="space-y-4">
        <TabsList>
          <TabsTrigger value="oportunidades">
            <Target className="w-4 h-4 mr-2" /> Oportunidades ({oportunidades.length})
          </TabsTrigger>
          <TabsTrigger value="licitaciones">
            <FileSearch className="w-4 h-4 mr-2" /> Licitaciones ({licitaciones.length})
          </TabsTrigger>
          <TabsTrigger value="leads">
            <Users className="w-4 h-4 mr-2" /> Leads ({leads.length})
          </TabsTrigger>
          <TabsTrigger value="recientes">
            <History className="w-4 h-4 mr-2" /> Recientes ({recientes.length})
          </TabsTrigger>
        </TabsList>

        {/* Oportunidades */}
        <TabsContent value="oportunidades">
          <div className="mb-3 flex items-center gap-3 theme-text-muted text-xs">
            <span>Filtrar por fecha (adjudicación/detección):</span>
            <DateRangeFilter
              value={fechaOportunidades}
              onChange={setFechaOportunidades}
              label="Fecha"
              compact
            />
            <span className="ml-auto">
              Mostrando {oportunidades.length} de {data?.mis_oportunidades?.length || 0}
            </span>
          </div>
          <Card className="theme-card">
            <CardContent className="p-0">
              {oportunidades.length === 0 ? (
                <div className="p-12 text-center theme-text-muted">
                  <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No tienes oportunidades asignadas activas.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b theme-border text-xs uppercase theme-text-muted">
                      <th className="text-left py-3 px-4">Título</th>
                      <th className="text-left py-3 px-4 hidden md:table-cell">Órgano</th>
                      <th className="text-right py-3 px-4 w-32">Importe</th>
                      <th className="text-center py-3 px-4 w-32">Estado</th>
                      <th className="text-center py-3 px-4 w-12">Ver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oportunidades.map((op) => (
                      <tr
                        key={op.oportunidad_id || op.expediente_id}
                        className="border-b theme-border hover:theme-bg-tertiary transition cursor-pointer"
                        onClick={() => irA("oportunidad", op.oportunidad_id, op.titulo)}
                      >
                        <td className="py-3 px-4">
                          <div className="theme-text font-medium line-clamp-1">
                            {op.titulo || "(sin título)"}
                          </div>
                          {op.adjudicatario && (
                            <div className="text-xs theme-text-muted">
                              Ganador: {op.adjudicatario}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell text-xs theme-text-muted truncate max-w-xs">
                          {op.organo_contratante}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm whitespace-nowrap theme-text">
                          {formatEUR(op.importe)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={`text-xs ${ESTADO_COLORS[op.estado] || ESTADO_COLORS.descartada}`}>
                            {op.estado}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center theme-text-muted">
                          <Eye className="w-4 h-4 inline" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Licitaciones */}
        <TabsContent value="licitaciones">
          <div className="mb-3 flex items-center gap-3 theme-text-muted text-xs">
            <span>Filtrar por plazo presentación:</span>
            <DateRangeFilter
              value={fechaLicitaciones}
              onChange={setFechaLicitaciones}
              label="Plazo"
              compact
            />
            <span className="ml-auto">
              Mostrando {licitaciones.length} de {data?.mis_licitaciones?.length || 0}
            </span>
          </div>
          <Card className="theme-card">
            <CardContent className="p-0">
              {licitaciones.length === 0 ? (
                <div className="p-12 text-center theme-text-muted">
                  <FileSearch className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No tienes licitaciones asignadas activas.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b theme-border text-xs uppercase theme-text-muted">
                      <th className="text-left py-3 px-4">Objeto</th>
                      <th className="text-left py-3 px-4 w-28">Plazo</th>
                      <th className="text-right py-3 px-4 w-32">Importe</th>
                      <th className="text-center py-3 px-4 w-32">Estado</th>
                      <th className="text-center py-3 px-4 w-12">Ver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {licitaciones.map((lic) => {
                      const dias = diasHasta(lic.fecha_limite_presentacion);
                      return (
                        <tr
                          key={lic.expediente_id || lic.expediente}
                          className="border-b theme-border hover:theme-bg-tertiary transition cursor-pointer"
                          onClick={() => irA("licitacion", lic.expediente_id || lic.expediente, lic.objeto_contrato)}
                        >
                          <td className="py-3 px-4">
                            <div className="theme-text font-medium line-clamp-1">
                              {lic.objeto_contrato || "(sin objeto)"}
                            </div>
                            <div className="text-xs theme-text-muted truncate">
                              {lic.organo_contratacion}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-xs theme-text">
                              {formatFecha(lic.fecha_limite_presentacion)}
                            </div>
                            {dias !== null && dias <= 14 && (
                              <Badge
                                className={`text-xs ${
                                  dias <= 3
                                    ? "bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/50"
                                    : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40"
                                }`}
                              >
                                {dias <= 0 ? "VENCIDO" : `${dias}d`}
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-sm whitespace-nowrap theme-text">
                            {formatEUR(lic.presupuesto_base || lic.valor_estimado)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={`text-xs ${ESTADO_COLORS[lic.estado] || ESTADO_COLORS.descartada}`}>
                              {lic.estado}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-center theme-text-muted">
                            <Eye className="w-4 h-4 inline" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leads */}
        <TabsContent value="leads">
          <div className="mb-3 flex items-center gap-3 theme-text-muted text-xs">
            <span>Filtrar por última actualización:</span>
            <DateRangeFilter
              value={fechaLeads}
              onChange={setFechaLeads}
              label="Fecha"
              compact
            />
            <span className="ml-auto">
              Mostrando {leads.length} de {data?.mis_leads?.length || 0}
            </span>
          </div>
          <Card className="theme-card">
            <CardContent className="p-0">
              {leads.length === 0 ? (
                <div className="p-12 text-center theme-text-muted">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No tienes leads asignados activos.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b theme-border text-xs uppercase theme-text-muted">
                      <th className="text-left py-3 px-4">Empresa</th>
                      <th className="text-left py-3 px-4 hidden md:table-cell">Contacto</th>
                      <th className="text-center py-3 px-4 w-32">Etapa</th>
                      <th className="text-right py-3 px-4 w-32">Valor est.</th>
                      <th className="text-center py-3 px-4 w-12">Ver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr
                        key={lead.lead_id}
                        className="border-b theme-border hover:theme-bg-tertiary transition cursor-pointer"
                        onClick={() => irA("lead", lead.lead_id, lead.empresa)}
                      >
                        <td className="py-3 px-4 theme-text font-medium">
                          {lead.empresa || "(sin nombre)"}
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell text-xs theme-text-muted">
                          {lead.contacto_principal} · {lead.email}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={`text-xs ${ESTADO_COLORS[lead.etapa] || ESTADO_COLORS.descartada}`}>
                            {lead.etapa || "—"}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm whitespace-nowrap theme-text">
                          {formatEUR(lead.valor_estimado)}
                        </td>
                        <td className="py-3 px-4 text-center theme-text-muted">
                          <Eye className="w-4 h-4 inline" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recientes */}
        <TabsContent value="recientes">
          <Card className="theme-card">
            <CardContent className="p-0">
              {recientes.length === 0 ? (
                <div className="p-12 text-center theme-text-muted">
                  <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  Aún no hay vistas registradas. Cuando abras una oportunidad
                  o licitación, aparecerá aquí para que vuelvas rápido.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b theme-border text-xs uppercase theme-text-muted">
                      <th className="text-left py-3 px-4">Título</th>
                      <th className="text-center py-3 px-4 w-32">Tipo</th>
                      <th className="text-right py-3 px-4 w-40">Visto</th>
                      <th className="text-center py-3 px-4 w-12">Ver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recientes.map((r, i) => (
                      <tr
                        key={i}
                        className="border-b theme-border hover:theme-bg-tertiary transition cursor-pointer"
                        onClick={() => irA(r.item_type, r.item_id, r.item_title)}
                      >
                        <td className="py-3 px-4 theme-text font-medium line-clamp-1">
                          {r.item_title}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline" className="text-xs">
                            {r.item_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right text-xs theme-text-muted">
                          {r.viewed_at
                            ? new Date(r.viewed_at).toLocaleString("es-ES")
                            : "—"}
                        </td>
                        <td className="py-3 px-4 text-center theme-text-muted">
                          <Eye className="w-4 h-4 inline" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
